import { type AttachedFiles, type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BdsSection } from "@buildertrend/components";

import yup from "utilities/form/yup";

import { BTForm } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTEditor } from "commonComponents/btWrappers/editor/BTEditor/BTEditor";
import { type EditorContent } from "commonComponents/btWrappers/editor/editor.types";
import { AttachmentContainer } from "commonComponents/entity/media/AttachmentContainer/AttachmentContainer";
import {
    type IBTInjectedFormikProps,
    withFormikBT,
} from "commonComponents/utilities/Formik/WithFormikBT";
import { FormikValidationSummary } from "commonComponents/utilities/validationSummary/FormikValidationSummary/FormikValidationSummary";

import { DocumentInstanceType } from "handlers/models";

import { getSpecificationEditorConfig } from "entity/specification/common/specification.utils";
import SpecificationPublishModal from "entity/specification/common/SpecificationPublishModal";
import SpecificationUnpublishModal from "entity/specification/common/SpecificationUnpublishModal";
import SpecificationViewableByDisplay from "entity/specification/common/SpecificationViewableByDisplay";
import { UnpublishedChangesBanner } from "entity/specification/common/UnpublishedChangesBanner";
import {
    type ISpecificationFormValues,
    type SpecificationDetailsFormActions,
    type SpecificationDisplayActionType,
    SpecificationOwnerShareMode,
    SpecificationSubShareMode,
} from "entity/specification/Specification.api.types";

import "./SpecificationDisplay.less";

import { LinkedItemsSection } from "./LinkedItemsSection";

export const SpecificationValidators = yup.object().shape<ISpecificationFormValues>({
    specificationContent: yup
        .mixed()
        .editorContentValid({
            maxContentSize: 50000,
            hasBase64Images: true,
            hasUploadedImages: false,
        })
        .label("Specification content"),
    ownerShareMode: yup.number().label("Share with Client"),
    subShareMode: yup.number().label("Share with Sub"),
    usersToNotify: yup.array().of(yup.number()),
});

interface ISpecificationDisplayProps {
    initialValues: ISpecificationFormValues;
    onSave: (
        values: ISpecificationFormValues,
        action?: SpecificationDetailsFormActions,
    ) => Promise<boolean>;
    attachedFiles: AttachedFiles | null;
    canViewPhotos: boolean;
    isBuilder: boolean;
    jobId: number;
    builderId: number;
    specificationId: number;
    externalId: string;
    readOnly: boolean;
    showViewableBySection?: boolean;
    showBidPackagesSection?: boolean;
    /** When true, renders the UnpublishedChangesBanner below the viewable-by section. */
    hasUnpublishedChanges?: boolean;
    /**
     * Attachments and related UI are treated as view-only (e.g., external users or a historical
     * snapshot preview). Note: this is separate from `readOnly`, which controls the editor itself.
     */
    isExternalMode?: boolean;
    isViewOnly?: boolean;
    className?: string;
    isMobileWebview?: boolean;
    isPublished?: boolean;

    activeAction?: SpecificationDisplayActionType;
    actionBeingPerformed?: SpecificationDetailsFormActions;
    onActionComplete?: (action: SpecificationDetailsFormActions) => void;
    onDirtyChange?: (dirty: boolean) => void;
    onAllFileChange?: (files: BTFileSystem[]) => void;
}

function SpecificationDisplayInternal({
    onSave,
    attachedFiles,
    canViewPhotos,
    isBuilder,
    jobId,
    builderId,
    specificationId,
    externalId,
    readOnly,
    showViewableBySection = true,
    showBidPackagesSection = true,
    hasUnpublishedChanges = false,
    isViewOnly = false,
    isExternalMode = false,
    className = "",
    isMobileWebview = false,
    isPublished = false,
    activeAction,
    actionBeingPerformed,
    onActionComplete,
    onDirtyChange,
    onAllFileChange,
    values,
    dirty,
    errors,
    submitCount,
    handleSubmit,
    validateForm,
    setFieldValue,
    setFieldTouched,
}: IBTInjectedFormikProps<ISpecificationDisplayProps, ISpecificationFormValues>) {
    useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    // Modal visibility state
    const [publishModalVisible, setPublishModalVisible] = useState(false);
    const [unpublishModalVisible, setUnpublishModalVisible] = useState(false);
    const [viewingPermissionsVisible, setViewingPermissionsVisible] = useState(false);
    // Bumped each time a modal opens so its instance remounts with fresh state, rather than
    // resetting via a useEffect - BTModal's destroyOnClose only tears down antd's internal modal
    // content, not the SpecificationPublishModal component itself.
    const [publishModalKey, setPublishModalKey] = useState(0);
    const [viewingPermissionsKey, setViewingPermissionsKey] = useState(0);

    const validateAndSave = useCallback(
        async (
            overrides?: Partial<ISpecificationFormValues>,
            action?: SpecificationDetailsFormActions,
        ): Promise<boolean> => {
            handleSubmit();
            const valuesToSave = overrides ? { ...values, ...overrides } : values;
            const formErrors = await validateForm(valuesToSave);
            if (Object.keys(formErrors).length > 0) {
                return false;
            }
            return onSave(valuesToSave, action);
        },
        [handleSubmit, validateForm, values, onSave],
    );

    const handlePublishAction = useCallback(() => {
        // Always show the publish modal, even when republishing an already-published spec -
        // it now also surfaces the notify-recipients picker for this update. Bump the key so a
        // reopened modal remounts with fresh share-mode/recipient state instead of reusing stale
        // state left over from the previous open.
        setPublishModalKey((key) => key + 1);
        setPublishModalVisible(true);
    }, []);

    // React to parent's requested action. Skip duplicate actions on remount.
    const prevActionRef = useRef(activeAction);
    useEffect(() => {
        if (!activeAction) {
            prevActionRef.current = undefined;
            return;
        }
        if (activeAction === prevActionRef.current) return;
        prevActionRef.current = activeAction;
        switch (activeAction) {
            case "publish":
                handlePublishAction();
                break;
            case "unpublish":
                setUnpublishModalVisible(true);
                break;
            case "viewingPermissions":
                setViewingPermissionsKey((key) => key + 1);
                setViewingPermissionsVisible(true);
                break;
            case "save":
                void (async () => {
                    const success = await validateAndSave(undefined, "save");
                    onActionComplete?.(success ? "save" : undefined);
                })();
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAction]);

    // Publish modal handlers
    const handlePublishModalClose = useCallback(() => {
        setPublishModalVisible(false);
        onActionComplete?.(undefined);
    }, [onActionComplete]);

    const handlePublish = useCallback(
        async (shareWithClients: boolean, shareWithSubs: boolean, usersToNotify: number[]) => {
            const success = await validateAndSave(
                {
                    ownerShareMode: shareWithClients
                        ? SpecificationOwnerShareMode.SharedWithAllOwners
                        : SpecificationOwnerShareMode.NotShared,
                    subShareMode: shareWithSubs
                        ? SpecificationSubShareMode.SharedWithAllSubs
                        : SpecificationSubShareMode.NotShared,
                    usersToNotify,
                },
                "publish",
            );
            if (success) {
                setPublishModalVisible(false);
                onActionComplete?.("publish");
            }
        },
        [validateAndSave, onActionComplete],
    );

    // Unpublish modal handlers
    const handleUnpublishModalClose = useCallback(() => {
        setUnpublishModalVisible(false);
        onActionComplete?.(undefined);
    }, [onActionComplete]);
    const handleUnpublish = useCallback(async () => {
        const success = await validateAndSave(
            {
                ownerShareMode: SpecificationOwnerShareMode.NotShared,
                subShareMode: SpecificationSubShareMode.NotShared,
            },
            "unpublish",
        );
        if (success) {
            setUnpublishModalVisible(false);
            onActionComplete?.("unpublish");
        }
    }, [validateAndSave, onActionComplete]);

    // Viewing permissions modal handlers
    const handleViewingPermissionsModalClose = useCallback(() => {
        setViewingPermissionsVisible(false);
        onActionComplete?.(undefined);
    }, [onActionComplete]);
    const handleViewingPermissionsUpdate = useCallback(
        async (shareWithClients: boolean, shareWithSubs: boolean, usersToNotify: number[]) => {
            const success = await validateAndSave(
                {
                    ownerShareMode: shareWithClients
                        ? SpecificationOwnerShareMode.SharedWithAllOwners
                        : SpecificationOwnerShareMode.NotShared,
                    subShareMode: shareWithSubs
                        ? SpecificationSubShareMode.SharedWithAllSubs
                        : SpecificationSubShareMode.NotShared,
                    usersToNotify,
                },
                "share",
            );
            if (success) {
                setViewingPermissionsVisible(false);
                onActionComplete?.("share");
            }
        },
        [validateAndSave, onActionComplete],
    );

    const attachmentsReadOnly = isViewOnly;

    const entity = useMemo(
        () =>
            attachedFiles
                ? {
                      documentInstanceType: DocumentInstanceType.Specification,
                      name: "Specification",
                      id: specificationId,
                      jobId: jobId,
                      builderId: builderId,
                      attachedFiles: attachedFiles,
                  }
                : null,
        [specificationId, jobId, builderId, attachedFiles],
    );

    const handleEditorChange = useCallback(
        (_field: string, value: EditorContent) => {
            setFieldValue("specificationContent", value);
        },
        [setFieldValue],
    );

    const handleEditorBlur = useCallback(() => {
        setFieldTouched("specificationContent", true);
    }, [setFieldTouched]);

    return (
        <>
            <div
                className={`specification-display ${className}`}
                data-testid="specification-display"
            >
                <BTForm onSubmit={handleSubmit}>
                    {!readOnly && (
                        <FormikValidationSummary
                            errors={errors}
                            values={values}
                            showAfterSubmit={submitCount}
                            scheme={SpecificationValidators}
                        />
                    )}
                    {showViewableBySection && isBuilder && (
                        <SpecificationViewableByDisplay
                            ownerShareMode={values.ownerShareMode}
                            subShareMode={values.subShareMode}
                        />
                    )}
                    {hasUnpublishedChanges && <UnpublishedChangesBanner />}

                    <BdsSection
                        title="Detailed scope and requirements"
                        testid="specificationDetailsSection"
                        className="bds-p-0 padding-top-md"
                    >
                        <div className="flex flex-row pb-3">
                            <div className="specification-content-editor">
                                <BTEditor
                                    id="specificationContent"
                                    data-testid="specificationContent"
                                    value={values.specificationContent}
                                    borderless
                                    onChange={handleEditorChange}
                                    onBlur={handleEditorBlur}
                                    extensionConfig={getSpecificationEditorConfig(isExternalMode, {
                                        jobId: jobId,
                                        canViewPhotosPermission: canViewPhotos,
                                    })}
                                    readOnly={readOnly}
                                />
                            </div>
                        </div>
                    </BdsSection>

                    {attachedFiles && entity && (!isViewOnly || attachedFiles.files.length > 0) && (
                        <BdsSection
                            title="Attachments"
                            testid="specificationAttachmentsSection"
                            className="bds-p-0"
                        >
                            <AttachmentContainer
                                entity={entity}
                                attachedFiles={attachedFiles}
                                isReadOnly={attachmentsReadOnly}
                                isOnExternalPage={isViewOnly}
                                idForFiles="attachedFiles"
                                onChangeForNewEntity={setFieldValue}
                                onAllFileChange={onAllFileChange}
                            />
                        </BdsSection>
                    )}

                    {showBidPackagesSection && isBuilder && readOnly && (
                        <LinkedItemsSection
                            specificationExternalId={externalId}
                            jobId={jobId}
                            isMobileWebview={isMobileWebview}
                        />
                    )}
                </BTForm>
            </div>
            {/* Modals — rendered outside the form div; BTModal portals to document.body */}
            <SpecificationPublishModal
                key={`share-${viewingPermissionsKey}`}
                visible={viewingPermissionsVisible}
                onClose={handleViewingPermissionsModalClose}
                onPublish={handleViewingPermissionsUpdate}
                isSubmitting={actionBeingPerformed === "share"}
                jobId={jobId}
                initialOwnerShareMode={values.ownerShareMode}
                initialSubShareMode={values.subShareMode}
                mode="share"
            />
            <SpecificationPublishModal
                key={`publish-${publishModalKey}`}
                visible={publishModalVisible}
                onClose={handlePublishModalClose}
                onPublish={handlePublish}
                isSubmitting={actionBeingPerformed === "publish"}
                jobId={jobId}
                initialOwnerShareMode={isPublished ? values.ownerShareMode : undefined}
                initialSubShareMode={isPublished ? values.subShareMode : undefined}
                mode="publish"
            />
            <SpecificationUnpublishModal
                visible={unpublishModalVisible}
                onClose={handleUnpublishModalClose}
                onUnpublish={handleUnpublish}
                isSubmitting={actionBeingPerformed === "unpublish"}
            />
        </>
    );
}

/**
 * Owns Formik state so both modal and inline editing share one form instance.
 * Manages publish/unpublish/viewing-permissions modals and save internally.
 * Parents drive actions via activeAction prop and respond via onActionComplete.
 */
export const SpecificationDisplay = withFormikBT<
    ISpecificationDisplayProps,
    ISpecificationFormValues
>({
    validationSchema: SpecificationValidators,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    mapPropsToValues: (props: ISpecificationDisplayProps) => props.initialValues,
    handleSubmit: () => {},
})(SpecificationDisplayInternal);
